import { useEffect, useRef } from "react";

export default function CDMorph() {
  const canvasRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const W = 340, H = 340, CX = 170, CY = 170;

    let phase = "cd";
    let hold = 0, t = 0, frame = 0;
    const HOLD = 60;
    const MORPH = 30;

    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
    const ease = t => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3)/2;

    function hslRgb(h,s,l){
      s/=100; l/=100;
      const k=n=>(n+h/30)%12;
      const a=s*Math.min(l,1-l);
      const f=n=>l-a*Math.max(-1,Math.min(k(n)-3,Math.min(9-k(n),1)));
      return [f(0)*255, f(8)*255, f(4)*255];
    }

    // ─── CD ─────────────────────────────────────────
    function drawCD(alpha){
      const outerR = 155;
      const dataR = 42;
      // const hubR = 32;
      const holeR = 16;

      ctx.save();
      ctx.globalAlpha = alpha;

      // base gradient
      const baseGrad = ctx.createRadialGradient(CX-30, CY-40, 10, CX, CY, outerR);
      baseGrad.addColorStop(0, '#e8e8ea');
      baseGrad.addColorStop(0.3, '#c8c8cc');
      baseGrad.addColorStop(0.7, '#b0b0b5');
      baseGrad.addColorStop(1, '#888890');
      ctx.beginPath();
      ctx.arc(CX, CY, outerR, 0, Math.PI*2);
      ctx.fillStyle = baseGrad;
      ctx.fill();

      // grooves
      ctx.save();
      ctx.beginPath();
      ctx.arc(CX, CY, outerR, 0, Math.PI*2);
      ctx.clip();
      for (let r = dataR; r < outerR; r += 3) {
        const depth = 0.04 + 0.025 * Math.sin(r * 0.4);
        ctx.beginPath();
        ctx.arc(CX, CY, r, 0, Math.PI*2);
        ctx.strokeStyle = `rgba(0,0,0,${depth})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      ctx.restore();

      // iridescent sheen (THIS is the color magic)
      const sheenAngle = frame * 0.025;
      ctx.save();
      ctx.beginPath();
      ctx.arc(CX, CY, outerR, 0, Math.PI*2);
      ctx.arc(CX, CY, dataR, Math.PI*2, 0, true);
      ctx.clip();

      const iw = outerR * 2, ih = outerR * 2;
      const imgData = ctx.getImageData(CX - outerR, CY - outerR, iw, ih);
      const d = imgData.data;
      const OR2 = outerR*outerR, DR2 = dataR*dataR;

      for (let py = 0; py < ih; py++) {
        for (let px = 0; px < iw; px++) {
          const x = px - outerR, y = py - outerR;
          const dist2 = x*x + y*y;
          if (dist2 > OR2 || dist2 < DR2) continue;

          const dist = Math.sqrt(dist2);
          const angle = Math.atan2(y, x);
          let diff = ((angle - sheenAngle) + Math.PI*3) % (Math.PI*2) - Math.PI;

          const strength = Math.max(0, 1 - Math.abs(diff)/0.65);
          const s2 = strength * strength;
          if (s2 < 0.01) continue;

          const hue = ((diff * 720) + frame * 1.5 + dist * 0.8) % 360;
          const [sr,sg,sb] = hslRgb(hue, 88, 62);

          const i = (py * iw + px) * 4;
          const blend = s2 * 0.85;

          d[i] = d[i]*(1-blend) + sr*blend;
          d[i+1] = d[i+1]*(1-blend) + sg*blend;
          d[i+2] = d[i+2]*(1-blend) + sb*blend;
        }
      }

      ctx.putImageData(imgData, CX - outerR, CY - outerR);
      ctx.restore();

      // center hole
      ctx.beginPath();
      ctx.arc(CX, CY, holeR, 0, Math.PI*2);
      ctx.fillStyle = '#209CEE';
      ctx.fill();

      ctx.restore();
    }

    // ─── NFC CARD ───────────────────────────────────
    function drawNFC(alpha){
      const cw = 290, ch = 182, r = 14;
      const x0 = CX - cw/2, y0 = CY - ch/2;

      ctx.save();
      ctx.globalAlpha = alpha;

      ctx.shadowColor = 'rgba(0,200,255,0.12)';
      ctx.shadowBlur = 28;

      ctx.beginPath();
      ctx.roundRect(x0, y0, cw, ch, r);

      const grad = ctx.createLinearGradient(x0, y0, x0+cw, y0+ch);
      grad.addColorStop(0, '#1c1c1c');
      grad.addColorStop(1, '#0e0e0e');

      ctx.fillStyle = grad;
      ctx.fill();
      ctx.shadowBlur = 0;

      // NFC arcs
      const nx = x0 + cw * 0.73, ny = CY;
      const pulse = 0.55 + 0.45 * Math.sin(frame * 0.07);

      [22,38,54].forEach((rr,i)=>{
        ctx.beginPath();
        ctx.arc(nx, ny, rr, -Math.PI*0.65, Math.PI*0.65);
        ctx.strokeStyle = `rgba(0,220,255,${(0.8 - i*0.22)*pulse})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      });

      ctx.restore();
    }

    // function drawDissolve(progress){
    //   const p = ease(progress);
    //   const wipeR = p * 200;

    //   const grad = ctx.createRadialGradient(CX, CY, 0, CX, CY, wipeR);
    //   grad.addColorStop(0, "rgba(13,13,13,0)");
    //   grad.addColorStop(1, "rgba(13,13,13,0.4)");

    //   ctx.beginPath();
    //   ctx.arc(CX, CY, 200, 0, Math.PI*2);
    //   ctx.fillStyle = grad;
    //   ctx.fill();
    // }

    function draw(){
      ctx.clearRect(0,0,W,H);

      if (phase === "cd") drawCD(1);
      else if (phase === "nfc") drawNFC(1);
      else if (phase === "morphing") {
        const p = ease(t);
        drawCD(1-p);
        drawNFC(p);
        //drawDissolve(t);
      }
      else if (phase === "morphing-back") {
        const p = ease(t);
        drawNFC(1-p);
        drawCD(p);
        //drawDissolve(t);
      }
    }

    function tick(){
      frame++;

      if (phase === "cd") {
        hold++;
        if (hold >= HOLD) { hold = 0; phase = "morphing"; t = 0; }
      }
      else if (phase === "morphing") {
        t = clamp(t + 1/MORPH, 0, 1);
        if (t >= 1) {
          phase = "nfc";
          hold = 0;
          if(labelRef.current){
            labelRef.current.textContent = "JUUG";
            labelRef.current.style.color = "#00dcff";
          }
      }
      }
      else if (phase === "nfc") {
        hold++;
        if (hold >= HOLD) { hold = 0; phase = "morphing-back"; t = 0; }
      }
      else if (phase === "morphing-back") {
        t = clamp(t + 1/MORPH, 0, 1);
        if (t >= 1) {
          phase = "cd";
          hold = 0;
          if(labelRef.current){
          labelRef.current.textContent = "COMPACT DISC";
          labelRef.current.style.color = "#555";
          }
        }
      }

      draw();
      requestAnimationFrame(tick);
    }

    tick();
  }, []);

  return (
    <div style={{
      background: "#209CEE",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <canvas ref={canvasRef} width={340} height={340}/>
      <div ref={labelRef} style={{
        marginTop: 22,
        fontSize: 11,
        letterSpacing: 4,
        color: "#555"
      }}>
        COMPACT DISC
      </div>
    </div>
  );
}